// react
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

// components
import { Avatar, Icon, Tooltip } from '@/components/atoms';

// hooks/
import { useWindowSize } from '@/hooks';

// theme
import { theme } from '@/theme';

// store
import { Account, CareCircleMember, Role } from '@karehero/models';

enum CareCircleSize {
  Small = 250,
  Medium = 350,
  Large = 450,
}

/**
 * CareCircle is a visual representation of the user's care circle.
 */
export const CareCircle = ({
  onAdd,
  onPreview,
  members,
  careRecipientMember,
}: CareCircleProps) => {
  // refs
  const careCircleMembers = useRef<HTMLDivElement>(null);

  // hooks
  const [width] = useWindowSize();

  // memo
  const circleSize = useMemo(() => {
    if (width > theme.breakpoint.mdValue) {
      return CareCircleSize.Large;
    } else if (width > theme.breakpoint.smValue) {
      return CareCircleSize.Medium;
    } else {
      return CareCircleSize.Small;
    }
  }, [width]);

  const size = useMemo(() => {
    switch (circleSize) {
      case CareCircleSize.Small:
        return 'sm';
      case CareCircleSize.Medium:
        return 'md';
      case CareCircleSize.Large:
        return 'lg';
    }
  }, [circleSize]);

  const fontSize = useMemo(() => {
    switch (circleSize) {
      case CareCircleSize.Small:
        return 24;
      case CareCircleSize.Medium:
        return 36;
      case CareCircleSize.Large:
        return 48;
    }
  }, [circleSize]);

  const careRecipientAvatarSize = useMemo(() => {
    switch (circleSize) {
      case CareCircleSize.Small:
        return 'md';
      case CareCircleSize.Medium:
        return 'lg';
      case CareCircleSize.Large:
        return 'xl';
    }
  }, [circleSize]);

  // effects
  useEffect(() => {
    if (careCircleMembers.current) {
      const memberElements: HTMLCollection = careCircleMembers.current.children;
      const angle = (2 * Math.PI) / (memberElements.length + 1);
      const radius = circleSize / 2;
      const offset = (1 / 2) * Math.PI - angle;

      for (let i = 0; i < memberElements.length; i++) {
        const memberElement: HTMLDivElement = memberElements[
          i
        ] as HTMLDivElement;
        const width = memberElement.offsetWidth;
        const height = memberElement.offsetHeight;
        const x = radius * Math.cos(angle * i - offset) - width / 2;
        const y = radius * Math.sin(angle * i - offset) - height / 2;

        memberElement.style.transform = `translate(${x}px, ${y}px)`;
      }
    }
  }, [careCircleMembers?.current?.children, members, circleSize]);

  return (
    <Wrapper>
      <Label>Add new member</Label>
      <StyledCareCircle size={circleSize}>
        <Circle />
        <Tooltip
          isSimple={false}
          content={
            <>
              <TooltipTitle>
                {careRecipientMember?.account?.firstName || ''}{' '}
                {careRecipientMember?.account.lastName || ''}
              </TooltipTitle>
              <TooltipSubtitle>Care Recipient</TooltipSubtitle>
            </>
          }
        >
          <Avatar
            size={careRecipientAvatarSize}
            fallback={careRecipientMember.fallback || ''}
            src={careRecipientMember.imgSrc}
          />
        </Tooltip>
        <CareCircleMembers ref={careCircleMembers}>
          {members.map((member) => (
            <div key={member.id} onClick={member.onClick}>
              <Tooltip
                isSimple={false}
                content={
                  <>
                    <TooltipTitle>
                      {member?.account?.firstName} {member?.account.lastName}
                    </TooltipTitle>
                    <TooltipSubtitle>{member?.roles?.[0].name}</TooltipSubtitle>
                  </>
                }
              >
                <StyledAvatar
                  onClick={() =>
                    onPreview?.({
                      ...member,
                    } as unknown as CareCircleMember)
                  }
                  size={size}
                  fallback={member.fallback}
                  src={member.imgSrc}
                />
              </Tooltip>
            </div>
          ))}
        </CareCircleMembers>
        <StyledButton aria-label='add new member' onClick={onAdd} size={size}>
          <Icon icon='plus' fontSize={fontSize} />
        </StyledButton>
      </StyledCareCircle>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Label = styled.div`
  text-align: center;
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.primary[80]};
`;

interface StyledCareCircleProps {
  size: number;
}
const StyledCareCircle = styled.div<StyledCareCircleProps>`
  position: relative;
  margin: ${(props) => props.theme.spacing[64]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
`;

const Circle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 99999px;
  background: ${(props) => props.theme.color.neutral[20]};
  border: 8px solid ${(props) => props.theme.color.neutral[30]};
  z-index: -1;
`;

interface StyledButtonProps {
  size: 'sm' | 'md' | 'lg';
}
const StyledButton = styled.button<StyledButtonProps>`
  all: unset;
  position: absolute;
  top: 0%;
  right: 50%;
  border-radius: 9999px;

  ${(props) => {
    let size = 48;
    switch (props.size) {
      case 'sm':
        size = 48;
        break;
      case 'md':
        size = 60;
        break;
      case 'lg':
        size = 80;
        break;
    }
    return `
      width: ${size}px;
      min-width: ${size}px;
      height: ${size}px;
      min-height: ${size}px;
    `;
  }}

  background: ${(props) => props.theme.color.primary[30]};
  color: ${(props) => props.theme.color.neutral[100]};
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  -webkit-transform: translate(50%, -50%);
  -moz-transform: translate(50%, -50%);
  -ms-transform: translate(50%, -50%);
  -o-transform: translate(50%, -50%);
  transform: translate(50%, -50%);
  transition-duration: 0.2s;
  &:hover {
    transform: translate(50%, -50%) scale(1.1);
  }
`;

const CareCircleMembers = styled.div`
  position: absolute;
  & > * {
    position: absolute;
  }
`;

const StyledAvatar = styled(Avatar)`
  cursor: pointer;
  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.1);
  }
`;

const TooltipTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.primary[80]};
`;

const TooltipSubtitle = styled.div`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

export interface Member {
  id: string;
  account: Partial<Account>;
  roles?: Role[];
  fallback: string;
  imgSrc?: string;
  onClick?: () => void;
}

interface CareCircleProps {
  careRecipientMember: Member;
  members: Member[];
  onAdd?: () => void;
  onPreview?: (careCircleMember: CareCircleMember) => void;
}

CareCircle.displayName = 'CareCircle';

export default CareCircle;
